/**
 * @file fullScreenMenu.component.tsx
 * @path apps/website/src/components/layout/fullScreenMenu.component.tsx
 * @description Full-screen overlay menu strictly using the Sir Bastians Design System.
 */

import { capeTownMap } from "@/assets";
import { MAIN_NAV_ITEMS, PATHS } from "@/config/paths.config";
import { branding, siteMetadata, socialLinks } from "@/config/site.config";
import { motion, Variants } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { Link as RouterLink } from "react-router-dom";

// --- Type Definitions ---
interface FullScreenMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

// --- Animation Variants ---
const menuVariants: Variants = {
  hidden: { opacity: 0, pointerEvents: "none" as const },
  visible: {
    opacity: 1,
    pointerEvents: "auto" as const,
    transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
  },
};

const navListVariants: Variants = {
  hidden: { transition: { staggerChildren: 0.05, staggerDirection: -1 } },
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};

const navItemVariants: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", damping: 20, stiffness: 100 },
  },
};

/**
 * Menu Header with branded typography.
 */
const MenuHeader = ({ onClose }: { onClose: () => void }) => (
  <header className="flex items-center justify-between">
    <RouterLink
      to={PATHS.ROOT}
      onClick={onClose}
      className="flex items-center gap-4 transition-transform hover:scale-[1.02]"
    >
      <div className="flex w-full items-center gap-3 overflow-hidden">
        {/* Logo Container with Neuro-Bezel */}
        <div className="relative flex aspect-square size-10 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground neuro-bezel transition-transform group-hover:scale-105">
          <img
            src={branding.logoLight}
            alt={siteMetadata.title}
            className="size-full object-cover rounded-xl"
          />
        </div>

        {/* Brand Text using Nav Theme */}
        <div className="flex flex-1 flex-col justify-center space-y-2">
          <span className="truncate text-xl font-bold tracking-widest leading-none text-primary">
            {branding.title}
          </span>
          <span className="truncate text-xs font-bold uppercase leading-none tracking-[0.2em] text-muted-foreground">
            {branding.subtitle}
          </span>
        </div>
      </div>
    </RouterLink>
    <button
      onClick={onClose}
      className="pl-1.5 pt-2 md:p-4 transition-colors hover:text-accent text-foreground"
      aria-label="Close menu"
    >
      <X className="h-8 w-8" />
    </button>
  </header>
);

/**
 * Main Navigation list with Montserrat.
 */
const MenuNavigation = ({ onClose }: { onClose: () => void }) => (
  <nav className="w-full lg:col-span-2">
    <motion.ul
      className="flex flex-col items-center space-y-4 lg:items-end px-4"
      variants={navListVariants}
    >
      {MAIN_NAV_ITEMS.map((item) => (
        <motion.li key={item.name} variants={navItemVariants}>
          <RouterLink
            to={item.path}
            onClick={onClose}
            className="group relative inline-flex items-center text-4xl font-bold font-montserrat text-foreground transition-all duration-300 hover:text-accent sm:text-5xl lg:text-7xl"
          >
            {/* Subtle number or dot could go here if desired */}
            {item.name}
          </RouterLink>
        </motion.li>
      ))}
    </motion.ul>
  </nav>
);

/**
 * Menu Footer with theme-aware colors.
 */
const MenuFooter = () => (
  <footer className="w-full border-t border-border pt-8">
    <div className="flex flex-col gap-8 sm:flex-row sm:items-end sm:justify-between">
      <div className="text-center sm:text-left">
        <p className="mb-2 text-xs font-bold font-montserrat uppercase tracking-[0.2em] text-accent">
          Location
        </p>
        <p className="font-open-sans text-sm font-medium text-foreground">
          Cape Town, South Africa
        </p>
      </div>

      <ul className="flex items-center justify-center gap-6 sm:justify-start">
        {socialLinks.map((link) => (
          <li key={link.label}>
            <a
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-bold font-montserrat text-foreground transition-colors hover:text-accent group"
            >
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1" />
              {link.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  </footer>
);

export const FullScreenMenu = ({ isOpen, onClose }: FullScreenMenuProps) => {
  return (
    <motion.div
      className="fixed inset-0 z-[100]"
      variants={menuVariants}
      initial="hidden"
      animate={isOpen ? "visible" : "hidden"}
      // ✨ UPDATED: Replaced aria-hidden with the modern inert attribute
      // The "? true : undefined" syntax keeps TypeScript happy for boolean attributes
      inert={!isOpen ? true : undefined}
    >
      {/* Background with theme background color and backdrop blur */}
      <div className="absolute inset-0 bg-background/95 backdrop-blur-2xl" />

      <motion.div
        className="relative z-10 mx-auto flex h-full flex-col p-5 sm:p-10"
        variants={navListVariants}
      >
        <MenuHeader onClose={onClose} />

        <div className="relative grid flex-grow grid-cols-1 items-center gap-12 lg:grid-cols-5 py-12">
          {/* Map Section with Theme-aware border */}
          <div className="hidden justify-center lg:col-span-3 lg:flex">
            <div className="aspect-[16/10] w-full overflow-hidden rounded-[2rem] border border-border">
              <img
                src={capeTownMap}
                alt="Map of Cape Town"
                className="h-full w-full object-cover opacity-80 transition-all duration-700 hover:opacity-100"
              />
            </div>
          </div>
          <MenuNavigation onClose={onClose} />
        </div>

        <MenuFooter />
      </motion.div>
    </motion.div>
  );
};
